



















































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { SysKBMediaModule } from '@/store/modules/sysKBMedia';
import CreateKBMediaRequest from '@/models/KnowledgeBase/CreateKBMediaRequest';
import KBMediaType from '@/models/KnowledgeBase/KBMediaType';

@Component({
  name: 'MediaUploadDialog'
})
export default class MediaUploadDialog extends Vue {
  @Prop({ required: true })
  public dialog!: boolean;

  private valid = false;
  private uploading = false;
  private file: File | null = null;
  private altText = '';
  private tags: string[] = [];
  private previewUrl = '';
  private imageWidth = 0;
  private imageHeight = 0;

  private rules = {
    required: (value: File | null) => !!value || 'Please select a file',
    fileSize: (value: File | null) => {
      if (!value) return true;
      const maxSize = 100 * 1024 * 1024; // 100MB
      return value.size <= maxSize || 'File size must be less than 100MB';
    }
  };

  get internalDialog(): boolean {
    return this.dialog;
  }

  set internalDialog(value: boolean) {
    if (!value) {
      this.$emit('close');
    }
  }

  get uploadProgress(): number {
    return SysKBMediaModule.uploadProgress;
  }

  get fileType(): string {
    return this.file?.type || '';
  }

  get isImage(): boolean {
    return this.file?.type.startsWith('image/') || false;
  }

  get imageDimensions(): string {
    if (this.imageWidth > 0 && this.imageHeight > 0) {
      return `${this.imageWidth}x${this.imageHeight}`;
    }
    return '';
  }

  getMediaType(fileType: string): number {
    if (fileType.startsWith('image/')) {
      return KBMediaType.Image;
    } else if (fileType.startsWith('video/')) {
      return KBMediaType.Video;
    } else {
      return KBMediaType.File;
    }
  }

  onFileChange(file: File | null) {
    if (file && file.type.startsWith('image/')) {
      // Create preview
      this.previewUrl = URL.createObjectURL(file);

      // Get image dimensions
      const img = new Image();
      img.onload = () => {
        this.imageWidth = img.width;
        this.imageHeight = img.height;
      };
      img.src = this.previewUrl;
    } else {
      this.previewUrl = '';
      this.imageWidth = 0;
      this.imageHeight = 0;
    }
  }

  async upload() {
    if (!this.valid || !this.file) return;

    this.uploading = true;
    try {
      // Step 1: Create media entry and get signed URL
      const createRequest = new CreateKBMediaRequest();
      createRequest.originalFileName = this.file.name;
      createRequest.fileType = this.file.type;
      createRequest.fileSize = this.file.size;
      createRequest.tags = this.tags;
      createRequest.altText = this.altText;

      const createResponse = await SysKBMediaModule.createMedia(createRequest);

      // Step 2: Upload file to signed URL
      const uploadResponse = await SysKBMediaModule.uploadFile({
        uploadUrl: createResponse.uploadUrl,
        file: this.file
      });

      // Step 3: Confirm upload with generation ID
      await SysKBMediaModule.confirmUpload({
        mediaId: createResponse.mediaId,
        request: {
          generation: parseInt(uploadResponse.generation),
          width: this.imageWidth > 0 ? this.imageWidth : undefined,
          height: this.imageHeight > 0 ? this.imageHeight : undefined,
          isNewVersion: false
        }
      });

      this.$emit('uploaded', createResponse.mediaId);
      this.reset();
      this.internalDialog = false;
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      this.uploading = false;
    }
  }

  cancel() {
    if (!this.uploading) {
      this.reset();
      this.internalDialog = false;
    }
  }

  reset() {
    this.file = null;
    this.altText = '';
    this.tags = [];
    this.previewUrl = '';
    this.imageWidth = 0;
    this.imageHeight = 0;
    this.uploading = false;
    (this.$refs.form as any)?.resetValidation();
  }

  @Watch('dialog')
  onDialogChange(newVal: boolean) {
    if (!newVal) {
      this.reset();
    }
  }
}
